import React, { useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { AUTH_API_URL } from '../config/api';
import { useLoginOptions } from '../hooks/useLoginOptions';

const ForgotPasswordPage = () => {
  const { loaded: optionsLoaded, passwordLoginEnabled } = useLoginOptions();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      // Make sure CSRF cookie is present before POSTing.
      try {
        await axios.get(`${AUTH_API_URL}csrf/`, { withCredentials: true });
      } catch (_) {
        // non-fatal
      }
      await axios.post(`${AUTH_API_URL}forgot-password/`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Something went wrong. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Password sign-in is off: members use Google, so there is nothing to reset here.
  if (optionsLoaded && !passwordLoginEnabled) return <Navigate to="/login" replace />;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        // Phones: flex-start + the card's auto margins still centre it, but let
        // the page scroll (soft keyboard) instead of clipping the card's top.
        alignItems: { xs: 'flex-start', sm: 'center' },
        overflowY: { xs: 'auto', sm: 'visible' },
        px: { xs: 2, sm: 0 },
        py: { xs: 2, sm: 0 },
        bgcolor: theme.palette.background.default,
        zIndex: 1300
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={6}
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 420,
          width: { xs: '100%', sm: '90%' },
          my: { xs: 'auto', sm: 0 },
          borderRadius: 2,
          // Phones: 16px inputs stop iOS zooming on focus; taller link tap target.
          [theme.breakpoints.down('sm')]: {
            '& .MuiInputBase-input': { fontSize: 16 },
            '& .MuiLink-root': { display: 'inline-block', py: 1 },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
          <img src="/gva_logo_v2.png" alt="GVA" style={{ height: '3.5rem', width: 'auto' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1.2 }}>Time</Typography>
            <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1.2 }}>Tracker</Typography>
          </Box>
        </Box>

        <Typography variant="h6" align="center" sx={{ mb: 1 }}>
          Forgot password?
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email and we'll send you a link to reset your password. The link expires in 10 minutes.
        </Typography>

        {submitted ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            If an account with that email exists, a reset link has been sent. Please check your inbox.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: theme.palette.primary.contrastText }} /> : 'Send reset link'}
            </Button>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            >
            Back to login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
